import { createContext, useCallback, useContext, useEffect, useState } from 'react';

type CollapsibleGlobalState = {
  updateIsOpen: (key: string, status: boolean | ((prev: boolean) => boolean)) => void;
  subscribe: (key: string, dispatch: (state: boolean) => void, defaultState: boolean) => void;
};

export type CollapsibleCallback = (status: boolean) => void;
export type CollapsibleRegistration = {
  state: boolean;
  callback: CollapsibleCallback;
};

const subscribers = new Map<string, CollapsibleRegistration>();
const registerCollapsible = (key: string, callback: CollapsibleCallback, defaultState: boolean) => {
  subscribers.set(key, { state: defaultState, callback });

  return () => subscribers.delete(key);
};
const updateCollapsibleState = (key: string, state: boolean | ((prev: boolean) => boolean)) => {
  const subscription = subscribers.get(key);

  if (!subscription) {
    return;
  }

  if (typeof state === 'function') {
    subscription.state = state(subscription.state);
  } else {
    subscription.state = state;
  }

  subscription.callback(subscription.state);
  // setOpenElementIds(subscribers.filter(([key, value]) => value.status === true)).map(([key]) => key)
};

export const CollapsibleGlobalStateContext = createContext<CollapsibleGlobalState>({
  updateIsOpen: updateCollapsibleState,
  subscribe: registerCollapsible,
});

export const useCollapsibleGlobalStateContext = () => useContext(CollapsibleGlobalStateContext);

export function useGlobalCollapsible(collapsibleBlockId: string, initialState: boolean) {
  const { updateIsOpen, subscribe } = useContext(CollapsibleGlobalStateContext);
  const [isOpen, setIsOpen] = useState(initialState);

  useEffect(() => {
    return subscribe(collapsibleBlockId, setIsOpen, initialState);
  }, [collapsibleBlockId, initialState, subscribe]);

  const toggle = useCallback(() => updateIsOpen(collapsibleBlockId, prev => !prev), [collapsibleBlockId, updateIsOpen]);
  const open = useCallback(() => updateIsOpen(collapsibleBlockId, true), [collapsibleBlockId, updateIsOpen]);
  const close = useCallback(() => updateIsOpen(collapsibleBlockId, false), [collapsibleBlockId, updateIsOpen]);

  return { isOpen, toggle, open, close };
}
